import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LayoutDashboard, CalendarClock, CircleDollarSign, FileSpreadsheet, ShieldCheck, Camera, ClipboardCheck, MessageSquare, Wrench, Menu, X, LogOut, KeyRound, ChevronDown, MapPin, Check } from 'lucide-react';
import { usePortal } from './PortalApp';
import { apiError, portalApi, portalFileUrl } from './portalApi';
import { Modal } from './portalUi';

export const NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard, to: '/portal', end: true },
  { key: 'schedule', label: 'Schedule', icon: CalendarClock, to: '/portal/schedule' },
  { key: 'hours', label: 'Hours & Costs', icon: CircleDollarSign, to: '/portal/hours' },
  { key: 'invoices', label: 'Invoices', icon: FileSpreadsheet, to: '/portal/invoices' },
  { key: 'safety', label: 'Safety & Tickets', icon: ShieldCheck, to: '/portal/safety' },
  { key: 'photos', label: 'Photos', icon: Camera, to: '/portal/photos' },
  { key: 'inspections', label: 'Inspections', icon: ClipboardCheck, to: '/portal/inspections' },
  { key: 'messages', label: 'Messages', icon: MessageSquare, to: '/portal/messages' },
  { key: 'request', label: 'Request Service', icon: Wrench, to: '/portal/request', highlight: true },
];

const initials = (n) => (n || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();

const ChangePassword = ({ onClose }) => {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post('/auth/change-password', { current_password: cur, new_password: nw });
      toast.success('Password updated');
      onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Change password" onClose={onClose} testId="portal-change-password">
      <form onSubmit={submit} className="space-y-3">
        <label className="block"><span className="p-overline">Current password</span>
          <input type="password" className="p-input mt-1" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="portal-cp-current" /></label>
        <label className="block"><span className="p-overline">New password (8+ characters)</span>
          <input type="password" className="p-input mt-1" value={nw} onChange={(e) => setNw(e.target.value)} minLength={8} required data-testid="portal-cp-new" /></label>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="p-btn p-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="p-btn p-btn-primary" disabled={busy} data-testid="portal-cp-save">{busy ? 'Saving…' : 'Update password'}</button>
        </div>
      </form>
    </Modal>
  );
};

export default function PortalLayout() {
  const { me, logout, site, setSite } = usePortal();
  const navigate = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [menu, setMenu] = useState(false);
  const [siteMenu, setSiteMenu] = useState(false);
  const [cp, setCp] = useState(false);
  const [unread, setUnread] = useState(0);
  const customer = me?.customer || {};
  const user = me?.user || {};
  const sites = customer.sites || [];
  const hasSites = sites.length > 1;
  const activeSite = hasSites ? sites.find((s) => s.name === site) : null;
  const logoSrc = customer.logo_updated_at ? `${portalFileUrl('/logo')}&v=${encodeURIComponent(customer.logo_updated_at)}` : null;
  const [unplaced, setUnplaced] = useState([]);
  useEffect(() => {
    if (!hasSites || !site) { setUnplaced([]); return undefined; }
    let on = true;
    portalApi.get('/summary').then((r) => { if (on) setUnplaced(r.data.unplaced_work_orders || []); }).catch(() => {});
    return () => { on = false; };
  }, [hasSites, site]);

  const siteSwitcher = (mobile = false) => hasSites && (
    <div className={mobile ? 'px-3 pt-3' : 'relative'}>
      {mobile && <div className="p-overline mb-1.5 px-1">Viewing</div>}
      <button type="button" onClick={() => setSiteMenu((v) => !v)} data-testid="portal-site-switcher"
        className={mobile
          ? 'w-full flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-800'
          : 'hidden sm:flex items-center gap-1.5 rounded-lg border border-blue-300/30 bg-white/5 hover:bg-white/10 px-2.5 h-9 text-sm font-semibold max-w-[220px]'}>
        <MapPin className={`w-4 h-4 shrink-0 ${mobile ? 'text-blue-700' : 'text-blue-200'}`} />
        <span className="truncate">{site ? site : 'All sites'}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 ${mobile ? 'text-slate-500' : 'text-blue-200'}`} />
      </button>
      {siteMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setSiteMenu(false)} />
          <div className={`${mobile ? 'relative mt-1' : 'absolute right-0 mt-2 w-72'} bg-white text-slate-900 rounded-xl border border-slate-200 shadow-xl z-50 py-1 p-rise`} data-testid="portal-site-menu">
            <button type="button" onClick={() => { setSite(''); setSiteMenu(false); setDrawer(false); }} data-testid="portal-site-option-all"
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center justify-between gap-2 ${!site ? 'font-bold text-blue-700' : ''}`}>
              <span>All sites <span className="text-xs text-slate-500 font-normal">· everything at a glance</span></span>{!site && <Check className="w-4 h-4" />}
            </button>
            <div className="my-1 border-t border-slate-100" />
            {sites.map((s) => (
              <button key={s.name} type="button" onClick={() => { setSite(s.name); setSiteMenu(false); setDrawer(false); }} data-testid={`portal-site-option-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center justify-between gap-2 ${site === s.name ? 'font-bold text-blue-700' : ''}`}>
                <span className="min-w-0"><span className="block truncate">{s.name}</span>{s.address && <span className="block text-xs text-slate-500 font-normal truncate">{s.address}</span>}</span>
                {site === s.name && <Check className="w-4 h-4 shrink-0" />}
              </button>))}
          </div>
        </>
      )}
    </div>
  );

  useEffect(() => {
    let on = true;
    const tick = async () => { try { const r = await portalApi.get('/messages/unread'); if (on) setUnread(r.data.unread || 0); } catch (e) { /* silent */ } };
    tick();
    const t = setInterval(tick, 30000);
    return () => { on = false; clearInterval(t); };
  }, []);

  const signOut = () => { logout(); navigate('/portal/login'); };

  const navLink = (n, mobile = false) => (
    <NavLink key={n.key} to={n.to} end={n.end} onClick={() => setDrawer(false)} data-testid={`portal-nav-${n.key}`}
      className={({ isActive }) => mobile
        ? `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-semibold ${isActive ? 'bg-[#0B192C] text-white' : n.highlight ? 'text-blue-700 bg-blue-50' : 'text-slate-700 hover:bg-slate-100'}`
        : `relative flex items-center gap-2 px-3 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${isActive ? 'border-blue-600 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-900'} ${n.highlight && !isActive ? 'text-blue-700' : ''}`}>
      <n.icon className="w-4 h-4 shrink-0" />
      <span>{n.label}</span>
      {n.key === 'messages' && unread > 0 && <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-bold px-1" data-testid="portal-unread-badge">{unread}</span>}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex flex-col" data-testid="portal-layout">
      <header className="h-16 bg-[#0B192C] text-white border-b border-[#1E2E48] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" className="md:hidden h-10 w-10 -ml-2 inline-flex items-center justify-center rounded-lg hover:bg-white/10" onClick={() => setDrawer(true)} aria-label="Menu" data-testid="portal-menu-btn"><Menu className="w-5 h-5" /></button>
            <Link to="/portal" className="flex items-center gap-3 min-w-0">
              <img src="/365-logo-white.png" alt="365 Industrial Solutions" className="h-9 w-auto" />
              <span className="hidden sm:block h-6 w-px bg-white/20" />
              <span className="hidden sm:inline-flex p-mono text-[10px] tracking-[0.2em] font-bold text-blue-200 border border-blue-300/30 rounded px-2 py-0.5">CLIENT PORTAL</span>
            </Link>
          </div>
          <div className="hidden lg:flex items-center gap-3 min-w-0" data-testid="portal-customer-badge">
            {logoSrc && <span className="inline-flex items-center justify-center bg-white rounded-lg px-2 py-1 shrink-0" data-testid="portal-customer-logo-plate"><img src={logoSrc} alt={`${customer.name} logo`} className="h-8 max-w-[160px] w-auto object-contain" data-testid="portal-customer-logo" /></span>}
            <div className="flex flex-col min-w-0">
              <span className="p-overline !text-blue-300/80">Prepared for</span>
              <span className="p-display text-lg font-bold leading-tight truncate">{customer.name}{activeSite ? <span className="text-blue-200 font-semibold"> · {activeSite.name}</span> : hasSites ? <span className="text-blue-200 font-semibold"> · All sites</span> : null}</span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {siteSwitcher()}
            <Link to="/portal/messages" className="relative h-10 w-10 inline-flex items-center justify-center rounded-lg hover:bg-white/10" aria-label="Messages" data-testid="portal-bell">
              <MessageSquare className="w-5 h-5" />
              {unread > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 rounded-full bg-amber-500 text-[10px] font-bold px-1 inline-flex items-center justify-center">{unread}</span>}
            </Link>
            <div className="relative">
              <button type="button" onClick={() => setMenu((v) => !v)} className="flex items-center gap-2 rounded-xl pl-1 pr-2 py-1 hover:bg-white/10" data-testid="portal-user-menu">
                <span className="h-8 w-8 rounded-lg bg-blue-600 inline-flex items-center justify-center text-xs font-bold">{initials(user.full_name)}</span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-sm font-semibold">{user.full_name}</span>
                  {user.title && <span className="block text-[11px] text-blue-200">{user.title}</span>}
                </span>
                <ChevronDown className="w-4 h-4 text-blue-200" />
              </button>
              {menu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenu(false)} />
                  <div className="absolute right-0 mt-2 w-60 bg-white text-slate-900 rounded-xl border border-slate-200 shadow-xl z-50 py-1 p-rise" data-testid="portal-user-dropdown">
                    <div className="px-4 py-2.5 border-b border-slate-100">
                      <div className="text-sm font-bold truncate">{user.full_name}</div>
                      <div className="text-xs text-slate-500 truncate">{user.email}</div>
                    </div>
                    <button type="button" className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2" onClick={() => { setMenu(false); setCp(true); }} data-testid="portal-change-password-btn"><KeyRound className="w-4 h-4 text-slate-500" /> Change password</button>
                    <button type="button" className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 flex items-center gap-2 text-rose-700" onClick={signOut} data-testid="portal-logout-btn"><LogOut className="w-4 h-4" /> Sign out</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <nav className="hidden md:block bg-white border-b border-slate-200 sticky top-16 z-30" data-testid="portal-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto p-scroll">{NAV.map((n) => navLink(n))}</div>
      </nav>

      {drawer && (
        <div className="fixed inset-0 z-50 md:hidden" data-testid="portal-drawer">
          <div className="absolute inset-0 bg-[#0B192C]/60" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 w-[84%] max-w-xs bg-white shadow-2xl flex flex-col">
            <div className="h-16 px-4 flex items-center justify-between border-b border-slate-200 bg-[#0B192C] text-white">
              <div className="flex items-center gap-2.5 min-w-0">
                {logoSrc && <span className="inline-flex items-center justify-center bg-white rounded-md px-1.5 py-0.5 shrink-0"><img src={logoSrc} alt="" className="h-7 max-w-[96px] w-auto object-contain" /></span>}
                <div className="min-w-0"><div className="p-overline !text-blue-300/80">Prepared for</div><div className="font-bold truncate">{customer.name}</div></div>
              </div>
              <button type="button" onClick={() => setDrawer(false)} className="h-9 w-9 inline-flex items-center justify-center rounded-lg hover:bg-white/10" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            {!hasSites && activeSite && <div className="px-4 py-2 text-xs text-slate-500 flex items-center gap-1.5 border-b border-slate-100"><MapPin className="w-3.5 h-3.5" />{activeSite.name}{activeSite.address ? ` — ${activeSite.address}` : ''}</div>}
            {siteSwitcher(true)}
            <div className="p-3 space-y-1 overflow-y-auto">{NAV.map((n) => navLink(n, true))}</div>
          </div>
        </div>
      )}

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {hasSites && site && unplaced.length > 0 && (
          <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900" data-testid="site-unplaced-banner">
            <MapPin className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0">Viewing <strong>{site}</strong> only — {unplaced.length === 1 ? 'work order' : `${unplaced.length} work orders`} <strong className="p-mono">{unplaced.slice(0, 4).join(', ')}{unplaced.length > 4 ? ` +${unplaced.length - 4} more` : ''}</strong> {unplaced.length === 1 ? "isn't" : "aren't"} tied to a plant yet, so {unplaced.length === 1 ? 'it is' : 'they are'} only shown under All sites.</span>
            <button type="button" className="font-semibold underline-offset-2 hover:underline" onClick={() => setSite('')} data-testid="site-unplaced-view-all">Show all sites</button>
          </div>)}
        {/* keyed on the selected site so every page re-fetches when the customer switches plants */}
        <div key={site || '__all__'}><Outlet /></div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-slate-500">
          <span>© {new Date().getFullYear()} 365 Industrial Solutions Inc. · 365Ops Client Portal</span>
          <span>Numbers shown are live and before HST. Questions? <Link to="/portal/messages" className="text-blue-700 font-semibold">Message your PM</Link>.</span>
        </div>
      </footer>
      {cp && <ChangePassword onClose={() => setCp(false)} />}
    </div>
  );
}
